import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

function ReportsSection() {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [salesReport, setSalesReport] = useState(null);
  const [ordersReport, setOrdersReport] = useState(null);
  const [productsReport, setProductsReport] = useState(null);
  const [customersReport, setCustomersReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = dateRange.start && dateRange.end ? 
        `?start_date=${dateRange.start}&end_date=${dateRange.end}` : '';
      
      const [sales, orders, products, customers] = await Promise.all([
        axios.get(`/admin/reports/sales${params}`),
        axios.get(`/admin/reports/orders${params}`),
        axios.get('/admin/reports/products'),
        axios.get(`/admin/reports/customers${params}`)
      ]);

      setSalesReport(sales.data);
      setOrdersReport(orders.data);
      setProductsReport(products.data);
      setCustomersReport(customers.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to fetch reports: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const exportCSV = (data, filename) => {
    const csv = Object.entries(data).map(([key, value]) => `${key},${value}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    toast.success('✅ CSV exported successfully!');
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h2>
        <p className="text-gray-600">View sales, orders, products, and customer reports</p>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Range Filter</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <Button onClick={fetchReports} disabled={loading} className="bg-custom-orange hover:bg-custom-orange">
            {loading ? (
              <>
                <i className="pi pi-spin pi-spinner mr-2"></i>
                Loading...
              </>
            ) : (
              <>
                <i className="pi pi-chart-bar mr-2"></i>
                Generate Reports
              </>
            )}
          </Button>
        </div>
      </Card>

      {salesReport && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Sales Report</h3>
            <Button size="sm" onClick={() => exportCSV(salesReport, 'sales_report')}>
              Export CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-700">
                ${(salesReport.total_revenue / 100).toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded">
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-blue-700">{salesReport.total_orders}</p>
            </div>
          </div>
        </Card>
      )}

      {ordersReport && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Orders Report</h3>
            <Button size="sm" onClick={() => exportCSV(ordersReport.status_breakdown, 'orders_report')}>
              Export CSV
            </Button>
          </div>
          <div className="space-y-2">
            <p className="font-medium">Status Breakdown:</p>
            {Object.entries(ordersReport.status_breakdown).map(([status, count]) => (
              <div key={status} className="flex justify-between p-2 border rounded">
                <span className="capitalize">{status}</span>
                <Badge>{count}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {productsReport && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Product Sales Report</h3>
            <Button size="sm" onClick={() => exportCSV(productsReport.product_sales, 'products_report')}>
              Export CSV
            </Button>
          </div>
          <div className="space-y-2">
            {Object.entries(productsReport.product_sales).map(([product, data]) => (
              <div key={product} className="flex justify-between p-2 border rounded">
                <span>{product}</span>
                <div className="space-x-4">
                  <span className="text-gray-600">Qty: {data.quantity}</span>
                  <span className="text-green-600 font-medium">${(data.revenue / 100).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {customersReport && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Customer Report</h3>
            <Button size="sm" onClick={() => exportCSV({total_customers: customersReport.total_customers}, 'customers_report')}>
              Export CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-purple-50 rounded">
              <p className="text-sm text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-purple-700">{customersReport.total_customers}</p>
            </div>
            <div className="p-4 bg-indigo-50 rounded">
              <p className="text-sm text-gray-600">New Customers</p>
              <p className="text-2xl font-bold text-indigo-700">{customersReport.new_customers}</p>
            </div>
          </div>
          
          <div className="mb-6">
            <p className="font-medium mb-2">Top Spenders:</p>
            {customersReport.top_spenders.map((customer, idx) => (
              <div key={customer.user_id} className="flex justify-between p-2 border rounded mb-2">
                <div>
                  <span className="font-medium">#{idx + 1} {customer.name}</span>
                  <span className="text-sm text-gray-600 ml-2">({customer.email})</span>
                </div>
                <div>
                  <span className="text-green-600 font-medium">${(customer.total_spend / 100).toFixed(2)}</span>
                  <span className="text-gray-600 ml-2">({customer.order_count} orders)</span>
                </div>
              </div>
            ))}
          </div>

          {customersReport.all_users && customersReport.all_users.length > 0 && (
            <div>
              <p className="font-medium mb-3 text-lg">All Users ({customersReport.all_users.length})</p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2 text-sm font-medium">Name</th>
                      <th className="text-left p-2 text-sm font-medium">Email</th>
                      <th className="text-left p-2 text-sm font-medium">Role</th>
                      <th className="text-left p-2 text-sm font-medium">Phone</th>
                      <th className="text-left p-2 text-sm font-medium">Auth Provider</th>
                      <th className="text-right p-2 text-sm font-medium">Orders</th>
                      <th className="text-right p-2 text-sm font-medium">Total Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customersReport.all_users.map(user => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 text-sm">{user.name}</td>
                        <td className="p-2 text-sm">{user.email}</td>
                        <td className="p-2 text-sm">
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role || 'customer'}
                          </Badge>
                        </td>
                        <td className="p-2 text-sm text-gray-600">{user.phone || '—'}</td>
                        <td className="p-2 text-sm text-gray-600">{user.auth_provider || 'email'}</td>
                        <td className="p-2 text-sm text-right">{user.order_count}</td>
                        <td className="p-2 text-sm text-right font-medium text-green-600">
                          ${(user.total_spend / 100).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

export default ReportsSection;